import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Onboarding from "@/pages/Onboarding";
import ProfileEditor from "@/pages/ProfileEditor";
import PublicProfile from "@/pages/PublicProfile";
import Directory from "@/pages/Directory";
import AdminDashboard from "@/pages/AdminDashboard";
import MicroOnboarding from "@/pages/MicroOnboarding";
import Paywall from "@/pages/Paywall";
import ScoutApply from "@/pages/ScoutApply";
import ScoutDashboard from "@/pages/ScoutDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import GetProfile from "@/pages/GetProfile";
import ProfilePreview from "@/pages/ProfilePreview";
import AdminProfileEdit from "@/pages/AdminProfileEdit";
import VSL from "@/pages/VSL";
import Unlock from "@/pages/Unlock";
import Welcome from "@/pages/Welcome";
import PlayerManagement from "@/pages/PlayerManagement";
import ContactCapture from "@/pages/ContactCapture";
import Unsubscribe from "@/pages/Unsubscribe";
import Account from "@/pages/Account";
import ImpersonationBanner from "@/components/ImpersonationBanner";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user || user === false) return <Navigate to="/login" />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/vsl" element={<VSL />} />
      <Route path="/unlock" element={<Unlock />} />
      <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
      <Route path="/contact" element={<ProtectedRoute><ContactCapture /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/player-management" element={<PlayerManagement />} />
      <Route path="/get-profile" element={<GetProfile />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/register" element={<Auth mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/start" element={<MicroOnboarding />} />
      <Route path="/paywall" element={<Paywall />} />
      <Route path="/scout-apply" element={<ScoutApply />} />
      <Route path="/scout" element={<ProtectedRoute><ScoutDashboard /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><ProfileEditor /></ProtectedRoute>} />
      <Route path="/preview" element={<ProtectedRoute><ProfilePreview /></ProtectedRoute>} />
      <Route path="/player/:slug" element={<PublicProfile />} />
      <Route path="/directory" element={<Directory />} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/edit/:userId" element={<ProtectedRoute adminOnly><AdminProfileEdit /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
        <Toaster
          position="top-right"
          toastOptions={{
            style: { background: "#141414", color: "#F5F5F5", border: "1px solid #262626" },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

function Shell() {
  const location = useLocation();
  // Routes that render full-bleed with no navbar (ad landing / VSL)
  const chromeless = ["/vsl", "/player-management"].some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  if (chromeless) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <AppRoutes />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <ImpersonationBanner />
      <Navbar />
      <main className="pt-16">
        <AppRoutes />
      </main>
    </div>
  );
}
